import {
  fetchAccessToken,
  getJwt,
  isAccessTokenRequired,
  isAccessTokenExpired,
  removeJwt,
  request,
  setJwt,
} from '@pb/utils'
import { path } from 'ramda'

import appActions from 'App/duck/actions'
import appSelectors from 'App/duck/selectors'

const MAX_TOKEN_REQUESTS_COUNT = 1
const INTERVAL_MS = 100
let PENDING_TOKEN_REQUESTS = 0

class RefreshTokenError extends Error {
  constructor(message) {
    super(message)
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'response' does not exist on type 'Refres... Remove this comment to see the full error message
    this.response = { status: 401, data: [{ code: 'REFRESH_TOKEN_FAILED' }] }
  }
}

const handleAccessTokenRequest = async (tokenExpired = false): Promise<any> => {
  const { data: jwt, status } = await fetchAccessToken(tokenExpired)
  if (status === 200) {
    // If response status is 200, it means that hybris session is expired. Treat is a 401.
    // Status 201 means everything is ok
    removeJwt()
    throw new RefreshTokenError('Could not refresh accessToken')
  }
  return jwt
}

const setupNetworkInterceptors = ({ dispatch, getState }): any => {
  if (__DEMO_OR_DEV__) {
    // Delay the respons
    request.axiosInstance.interceptors.request.use(
      (config) =>
        new Promise((resolve) => {
          setTimeout(() => {
            resolve(config)
          }, 500)
        }),
      (error) => {
        return Promise.reject(error)
      },
    )
  }

  // If there was some response error, set that code in state
  request.axiosInstance.interceptors.response.use(
    (response) => {
      return response
    },
    (error) => {
      const statusCode: number = path(['response', 'status'])(error) as number
      const errorCode: string = path(['response', 'data', 0, 'code'])(error) as string

      dispatch(appActions.setXhrErrorCode({ statusCode, errorCode }))

      return Promise.reject(error)
    },
  )

  // This interceptor is a temporary solution until we use Microsoft MSAL library to refresh tokens.
  request.axiosInstance.interceptors.request.use(
    async (config) => {
      const isCiamEnabled = appSelectors.getIsCiamEnabled(getState())
      if (isCiamEnabled) {
        if (!isAccessTokenRequired(config.url)) {
          return config
        }

        return new Promise((resolve, reject) => {
          const interval = setInterval(async () => {
            if (PENDING_TOKEN_REQUESTS < MAX_TOKEN_REQUESTS_COUNT) {
              try {
                PENDING_TOKEN_REQUESTS += 1
                const jwt = getJwt()
                if (config?.headers?.Authorization) {
                  if (jwt && jwt.accessToken) {
                    // Refresh token if expired

                    if (isAccessTokenExpired(jwt.expiresOnDate)) {
                      const refreshedJwt = await handleAccessTokenRequest(true)
                      setJwt(refreshedJwt)
                      config.headers.Authorization = `Bearer ${refreshedJwt.accessToken}` as string
                    } else {
                      config.headers.Authorization = `Bearer ${jwt.accessToken}`
                    }
                  } else {
                    // Backup -> Fetch access token if authorization is required but no token exists.
                    const newJwt = await handleAccessTokenRequest()
                    setJwt(newJwt)
                    config.headers.Authorization = `Bearer ${newJwt.accessToken}`
                  }
                }
                PENDING_TOKEN_REQUESTS -= 1
                clearInterval(interval)
                resolve(config)
              } catch (e) {
                clearInterval(interval)
                reject(e)
              }
            }
          }, INTERVAL_MS)
        })
      }
      return config
    },
    (error) => {
      Promise.reject(error)
    },
  )
}

export default setupNetworkInterceptors
